// @flow

/**
 * Prepare Markdown for Export
 * Strips custom HTML tags (e.g., <prompt>) by applying registered transformers
 */

import { DEFAULT_EXPORT_TAG_TRANSFORMERS } from '../constants';
import type { TagTransformer } from '../types';

/**
 * Apply tag transformers to markdown text before export.
 * Each transformer replaces occurrences of <tagName>...</tagName> with the
 * result of its transform function.
 *
 * @param {string} markdown - Raw markdown that may contain custom HTML tags
 * @param {TagTransformer[]} transformers - Transformers to apply (defaults to DEFAULT_EXPORT_TAG_TRANSFORMERS)
 * @returns {string} Markdown with custom tags replaced
 */
export const prepareMarkdownForExport = (
  markdown: string,
  transformers: TagTransformer[] = DEFAULT_EXPORT_TAG_TRANSFORMERS,
): string => {
  let result = markdown;

  for (const { tagName, transform } of transformers) {
    const regex = new RegExp(`<${tagName}>([\\s\\S]*?)<\\/${tagName}>`, 'g');
    result = result.replace(regex, (_, content) => transform(content));
  }

  // Hardening: strip any remaining stray (unpaired) tags — e.g. from an aborted
  // stream that never closed a block, or an orphaned <data-display id="…"> whose
  // id failed to resolve — so no literal custom tag can leak into an export.
  for (const { tagName } of transformers) {
    result = result.replace(new RegExp(`</?${tagName}(\\s[^>]*)?>`, 'g'), '');
  }

  // Collapse blank lines between consecutive list items so they group
  // into a single list (prevents double-height spacing from transformed tags)
  result = result.replace(/^(- .+)\n\n(?=- )/gm, '$1\n');
  result = result.replace(/^(\d+\. .+)\n\n(?=\d+\. )/gm, '$1\n');

  return result;
};
